import React, { useState, useEffect } from "react";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { TrendingUp, Award, Target, CheckCircle2, XCircle, Package, Calendar, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function MyPerformancePage() {
  const { user } = useAuth();
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("30"); // days

  const fetchPerformance = async () => {
    setLoading(true);
    try {
      const fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - parseInt(period));
      
      const res = await api.get("/performance/my-performance", {
        params: {
          from_date: fromDate.toISOString().slice(0, 10),
          to_date: new Date().toISOString().slice(0, 10)
        }
      });
      setPerformance(res.data);
    } catch (e) {
      toast.error("Failed to load performance data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPerformance(); }, [period]);

  const summary = performance?.summary || {};
  const dailyData = performance?.daily_breakdown || [];

  return (
    <div className="p-4 md:p-6 animate-fade-up" data-testid="my-performance-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: "Manrope" }}>My Performance</h1>
          <p className="text-slate-500 text-sm">{performance?.employee_name || user?.full_name}</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={e => setPeriod(e.target.value)}
            className="field-select h-9 text-xs"
            data-testid="period-select"
          >
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 90 Days</option>
          </select>
          <button onClick={fetchPerformance} className="p-2 border border-slate-200 rounded-sm hover:bg-slate-50">
            <RefreshCw size={16} className={loading ? "animate-spin text-orange-500" : "text-slate-500"} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
          <RefreshCw size={24} className="mx-auto animate-spin text-orange-500 mb-2" />
          <p className="text-slate-500">Loading performance data...</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-sm p-4 text-white">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle2 size={20} className="opacity-80" />
                <span className="text-green-200 text-xs">Installations</span>
              </div>
              <div className="text-3xl font-black">{summary.mi_completed || 0}</div>
              <div className="text-green-100 text-xs">Total Commissioned</div>
            </div>

            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-sm p-4 text-white">
              <div className="flex items-center justify-between mb-2">
                <Target size={20} className="opacity-80" />
                <span className="text-blue-200 text-xs">FTR Rate</span>
              </div>
              <div className="text-3xl font-black">{summary.ftr_rate || 0}%</div>
              <div className="text-blue-100 text-xs">First Time Right</div>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-sm p-4 text-white">
              <div className="flex items-center justify-between mb-2">
                <Award size={20} className="opacity-80" />
                <span className="text-purple-200 text-xs">QC Passed</span>
              </div>
              <div className="text-3xl font-black">{summary.qc_passed || 0}</div>
              <div className="text-purple-100 text-xs">Quality Approved</div>
            </div>

            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-sm p-4 text-white">
              <div className="flex items-center justify-between mb-2">
                <Package size={20} className="opacity-80" />
                <span className="text-orange-200 text-xs">Meters</span>
              </div>
              <div className="text-3xl font-black">{summary.meters_installed || 0}</div>
              <div className="text-orange-100 text-xs">Installed</div>
            </div>
          </div>

          {/* Detailed Stats */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Lifecycle Breakdown */}
            <div className="bg-white border border-slate-200 rounded-sm">
              <div className="px-4 py-3 border-b border-slate-100">
                <h3 className="font-bold text-slate-800">Work Breakdown</h3>
              </div>
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Surveys Completed</span>
                  <span className="font-bold text-blue-600">{summary.surveys_completed || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">DTM (Meter Mounting)</span>
                  <span className="font-bold text-purple-600">{summary.dtm_completed || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">DTC (Connection)</span>
                  <span className="font-bold text-indigo-600">{summary.dtc_completed || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">MI (Commissioning)</span>
                  <span className="font-bold text-green-600">{summary.mi_completed || 0}</span>
                </div>
                <hr className="border-slate-100" />
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">QC Rejected (Rework)</span>
                  <span className="font-bold text-red-500">{summary.qc_rejected || 0}</span>
                </div>
              </div>
            </div>

            {/* Quality Stats */}
            <div className="bg-white border border-slate-200 rounded-sm">
              <div className="px-4 py-3 border-b border-slate-100">
                <h3 className="font-bold text-slate-800">Quality Performance</h3>
              </div>
              <div className="p-4">
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-slate-600">First Time Right (FTR)</span>
                    <span className="font-bold text-green-600">{summary.ftr_rate || 0}%</span>
                  </div>
                  <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-400 to-green-500 rounded-full transition-all"
                      style={{ width: `${summary.ftr_rate || 0}%` }}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div className="text-center p-3 bg-green-50 rounded">
                    <CheckCircle2 size={24} className="mx-auto text-green-500 mb-1" />
                    <div className="text-xl font-bold text-green-600">{summary.qc_passed || 0}</div>
                    <div className="text-xs text-green-700">QC Passed</div>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded">
                    <XCircle size={24} className="mx-auto text-red-500 mb-1" />
                    <div className="text-xl font-bold text-red-600">{summary.qc_rejected || 0}</div>
                    <div className="text-xs text-red-700">QC Rejected</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Daily Activity */}
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-4 py-3 border-b border-slate-100">
              <h3 className="font-bold text-slate-800">Daily Activity (Last 14 Days)</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="dt-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th className="text-center">Surveys</th>
                    <th className="text-center">DTM</th>
                    <th className="text-center">DTC</th>
                    <th className="text-center">MI</th>
                    <th className="text-center">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyData.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-8 text-slate-400">
                        <Calendar size={24} className="mx-auto mb-2" />
                        No activity data available
                      </td>
                    </tr>
                  ) : dailyData.map((day, i) => (
                    <tr key={i}>
                      <td className="font-mono text-xs">{day.date}</td>
                      <td className="text-center"><span className={`px-2 py-0.5 rounded text-xs font-bold ${day.surveys > 0 ? "bg-blue-100 text-blue-700" : "text-slate-400"}`}>{day.surveys}</span></td>
                      <td className="text-center"><span className={`px-2 py-0.5 rounded text-xs font-bold ${day.dtm > 0 ? "bg-purple-100 text-purple-700" : "text-slate-400"}`}>{day.dtm}</span></td>
                      <td className="text-center"><span className={`px-2 py-0.5 rounded text-xs font-bold ${day.dtc > 0 ? "bg-indigo-100 text-indigo-700" : "text-slate-400"}`}>{day.dtc}</span></td>
                      <td className="text-center"><span className={`px-2 py-0.5 rounded text-xs font-bold ${day.mi > 0 ? "bg-green-100 text-green-700" : "text-slate-400"}`}>{day.mi}</span></td>
                      <td className="text-center font-bold text-slate-800">{day.surveys + day.dtm + day.dtc + day.mi}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
